import { AppPermission } from "@closerplatform/spinner-openapi";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { observer } from "mobx-react";
import React from "react";
import commonStyles from "../../../containers/settings/settingsEditCommon.css";
import { useStore } from "../../../models";
import { withPermissionsFallback } from "../../HOC/withPermissionFallback/withPermissionFallback";
import { withPreloading } from "../../HOC/withPreloading/withPreloading";
import { SettingsListFilterProvider } from "../settingsForms/filters/useListFilter/useListFilter";
import { SubtitleDescription } from "../subtitleDescription";
import { AdviserEditScreen } from "./components/adviserEditScreen/adviserEditScreen";
import { ExistingAdviserForm } from "./types";
import { UsersSettingsList } from "./usersSettingsList";
import { UsersSettingsProvider, useUsersSettings } from "./useUsersSettings";
import { copyExistingAdviserForm } from "./utils/utils";

export const UsersSettingsComponent = observer(() => {
  const { currentlyEditedItem, discardEditedItem, items, updateItem } = useUsersSettings();
  const { uiStore } = useStore();

  const getDescription = (): string | undefined => {
    if (
      uiStore.canDisplayAdvisersConfiguration() &&
      uiStore.canDisplayAdvisersAssignments() &&
      uiStore.canDisplayAdvisersAutoAssignedLimit()
    ) {
      return intl.formatMessage("settings.advisers.list.description.full");
    }
    if (uiStore.canDisplayAdvisersAssignments()) {
      return intl.formatMessage("settings.advisers.list.description.assignments");
    }
    if (uiStore.canDisplayAdvisersAutoAssignedLimit()) {
      return intl.formatMessage("settings.advisers.list.description.capacity");
    }
    if (uiStore.canDisplayAdvisersConfiguration()) {
      return intl.formatMessage("settings.advisers.list.description.configuration");
    }
    if (uiStore.canDisplayAgentGroupsConfiguration()) {
      return intl.formatMessage("settings.advisers.list.description.groups");
    }
    return;
  };

  const renderList = () => (
    <>
      <div className={commonStyles.title}>
        <L10n id="settings.advisers" />
      </div>
      <div className={commonStyles.section}>
        <SubtitleDescription
          subtitle={intl.formatMessage("settings.advisers.list")}
          description={getDescription()}
        />
        <div className={commonStyles.content}>
          <UsersSettingsList />
        </div>
      </div>
    </>
  );

  const renderEditGroupScreen = (item: ExistingAdviserForm) => {
    const reset = (editedItem: ExistingAdviserForm) => {
      const initial = items.find((i) => i.$.id.$ === editedItem.$.id.$);

      if (initial) {
        editedItem.$.tagIds.reset(initial.$.tagIds.$);
        editedItem.$.tagGroupIds.reset(initial.$.tagGroupIds.$);
        editedItem.$.roleId.reset(initial.$.roleId.$);
        editedItem.$.validFrom.reset(initial.$.validFrom.$);
        editedItem.$.validTo.reset(initial.$.validTo.$);
        editedItem.$.autoAssignedLimit.reset(initial.$.autoAssignedLimit.$);
      }
    };

    return (
      <AdviserEditScreen
        adviser={item}
        onGoBack={discardEditedItem}
        reset={reset}
        save={(updatedItem) => updateItem(copyExistingAdviserForm(updatedItem))}
      />
    );
  };

  return currentlyEditedItem ? renderEditGroupScreen(currentlyEditedItem) : renderList();
});

const WithProvider = () => (
  <SettingsListFilterProvider<ExistingAdviserForm>>
    <UsersSettingsProvider>
      <UsersSettingsComponent />
    </UsersSettingsProvider>
  </SettingsListFilterProvider>
);

export const UsersSettings = withPermissionsFallback<{}>(
  [
    AppPermission.SettingsAdvisersConfiguration,
    AppPermission.SettingsAdvisersAssignments,
    AppPermission.SettingsAdvisersAutoAssignedLimit,
    AppPermission.SettingsAgentGroupsConfiguration
  ],
  withPreloading(WithProvider)
);
